/**
 * Fallback module (PRD §3 tiers 2-3, §3.2, §5, §7.2): GPT display slot with viewable refresh,
 * and the AdSense tier with hard teardown and container check.
 */
import { ADSENSE_URL, GPT_URL } from './config.js';
import { adsenseSize, fallbackSizeMapping } from './size-profile.js';

/**
 * Viewable-time refresh timer for the GPT fallback (§5, REF-2).
 * The timer runs only while the slot is >= 50% visible and the tab is visible.
 * @param {object} options
 * @param {number} options.refreshSeconds 30
 * @param {number} options.maxRefreshes 5
 * @param {Function} options.now
 * @param {Function} options.setTimeoutFn
 * @param {Function} options.clearTimeoutFn
 * @param {Function} options.onRefresh (refreshNumber) => void
 * @returns {object}
 */
export function createRefreshTimer({ refreshSeconds, maxRefreshes, now, setTimeoutFn, clearTimeoutFn, onRefresh }) {
  const state = { filled: false, slotViewable: false, tabVisible: true, accumulatedMs: 0, since: null, count: 0, stopped: false };
  let timer = null;

  const viewable = () => state.slotViewable && state.tabVisible;
  const canRun = () => !state.stopped && state.filled && state.count < maxRefreshes;

  /** Bank running time and clear the timer. */
  function pause() {
    if (state.since !== null) {
      state.accumulatedMs += now() - state.since;
      state.since = null;
    }
    clearTimeoutFn(timer);
    timer = null;
  }

  /** Start or continue the timer if every condition holds. */
  function resume() {
    if (!canRun() || !viewable() || state.since !== null) return;
    state.since = now();
    const remaining = Math.max(0, refreshSeconds * 1000 - state.accumulatedMs);
    timer = setTimeoutFn(fire, remaining);
  }

  /** The viewable timer reached 30 s: refresh (all conditions re-checked). */
  function fire() {
    pause();
    if (!canRun() || !viewable() || state.accumulatedMs < refreshSeconds * 1000) return;
    state.count += 1;
    state.accumulatedMs = 0;
    state.filled = false; // wait for the refreshed render before counting again
    onRefresh(state.count);
  }

  return {
    state,
    /** @param {number} percent GPT slotVisibilityChanged inViewPercentage */
    onVisibility(percent) {
      state.slotViewable = percent >= 50;
      if (viewable()) resume();
      else pause();
    },
    /** @param {boolean} visible document.visibilityState === 'visible' */
    onTabVisibility(visible) {
      state.tabVisible = visible;
      if (viewable()) resume();
      else pause();
    },
    /**
     * A render ended. Empty never refreshes (§5).
     * @param {boolean} filled
     */
    onRender(filled) {
      pause();
      state.filled = filled;
      if (!filled) {
        state.stopped = true;
        return;
      }
      state.accumulatedMs = 0;
      resume();
    },
    stop() {
      state.stopped = true;
      pause();
    },
  };
}

/**
 * §3.2 step 2: check a container before any AdSense request.
 * @param {object} input
 * @param {Element} input.container fresh div
 * @param {Element} input.slot slot shell
 * @param {Element} input.article article body (ancestor walk stops here)
 * @param {Window} input.win
 * @param {Array<{left:number,top:number,right:number,bottom:number}>} input.overlapRects fixed/sticky rects
 * @param {number} input.visibleRatio slot visible share 0..1
 * @param {[number, number]} [input.adSize] fixed AdSense unit size
 * @returns {string|null} name of the failed check, or null when every check passes
 */
export function checkAdsenseContainer({ container, slot, article, win, overlapRects, visibleRatio, adSize = [300, 250] }) {
  if (container.children.length) return 'container_not_empty';
  if (slot.querySelector('video,canvas,iframe,object,embed')) return 'slot_has_media';
  const rect = container.getBoundingClientRect();
  let node = container;
  while (node && node.nodeType === 1) {
    const style = win.getComputedStyle(node);
    if (style.position === 'fixed' || style.position === 'sticky') return 'ancestor_fixed_or_sticky';
    if (style.transform && style.transform !== 'none') return 'ancestor_transform';
    if (style.opacity !== '' && Number(style.opacity) < 1) return 'ancestor_opacity';
    if (style.visibility === 'hidden') return 'ancestor_hidden';
    if (style.display === 'none') return 'ancestor_display_none';
    // 2.0.3: overflow:hidden alone is not a problem (themes often set it on <article>). Block only when this
    // parent would actually clip the fixed-size unit (§3.2). The computed `overflow` covers both axes.
    if (/hidden|clip/.test(style.overflow)) {
      const outer = node.getBoundingClientRect();
      if (
        rect.left < outer.left ||
        rect.left + adSize[0] > outer.right ||
        rect.top < outer.top ||
        rect.top + adSize[1] > outer.bottom + 1
      )
        return 'ancestor_overflow_hidden';
    }
    if (node === article) break;
    node = node.parentElement;
  }
  for (const other of overlapRects) {
    if (rect.left < other.right && other.left < rect.right && rect.top < other.bottom && other.top < rect.bottom) {
      return 'overlapped';
    }
  }
  if (visibleRatio < 0.5) return 'not_visible';
  return null;
}

/**
 * Create the fallback controller for one slot.
 * @param {object} deps
 * @param {Window} deps.win
 * @param {Document} deps.doc
 * @param {object} deps.config final config
 * @param {string} deps.playerId
 * @param {Element} deps.slot slot shell (keeps its reserved height)
 * @param {Element} deps.article
 * @param {Function} deps.getProfile () => profile name
 * @param {Function} deps.loadScript (url, attrs) => Promise
 * @param {Function} deps.getVisibleRatio () => 0..1
 * @param {Function} deps.whenVisible () => Promise resolved once the slot is >= 50% visible
 * @param {Function} deps.getOverlapRects () => fixed/sticky rects
 * @param {Function} deps.isBelowViewport () => boolean (slot entirely below the viewport)
 * @param {Promise<object>} deps.consentPromise resolves to ConsentSignals (§2.1)
 * @param {boolean} deps.childDirected config privacy.childDirected
 * @param {object} deps.targeting key-values (site, qs_src, qs_cmp, player)
 * @param {Function} deps.now
 * @param {Function} deps.setTimeoutFn
 * @param {Function} deps.clearTimeoutFn
 * @param {Function} deps.MutationObserverCtor
 * @param {Function} deps.onEvent (name, params)
 * @param {Function} deps.onState (state, detail) slot state for UI
 * @returns {object}
 */
export function createFallback(deps) {
  const { win, doc, config, playerId, slot } = deps;
  const display = config.fallbackDisplay;
  const state = {
    tier: null,
    gptSlot: null,
    divId: null,
    adsenseDone: false,
    destroyed: false,
    refresh: null,
    renderTimer: null,
  };
  const adShell = () => slot.querySelector('.qsvp-ad-shell');

  /**
   * Hand the slot to the neutral placeholder (or collapse when allowed) and stop.
   * @param {string} reason
   */
  function finalEmpty(reason) {
    // WF-8 / §9: collapse only when the slot is entirely below the viewport.
    if (deps.isBelowViewport()) {
      deps.onState('collapsed', { reason });
      deps.onEvent('qsvp_collapse', {});
    } else {
      deps.onState('all-empty', { reason });
    }
  }

  /**
   * Destroy the GPT slot and remove its div, so no late AdX response renders (§3.1, §3.2 step 1).
   */
  function destroyGpt() {
    if (state.refresh) state.refresh.stop();
    deps.clearTimeoutFn(state.renderTimer);
    const googletag = win.googletag;
    if (state.gptSlot && googletag && typeof googletag.destroySlots === 'function') {
      try {
        googletag.destroySlots([state.gptSlot]);
      } catch (error) {
        // Already destroyed.
      }
    }
    state.gptSlot = null;
    const div = state.divId && doc.getElementById(state.divId);
    if (div) div.remove();
  }

  /**
   * Tier 3: AdSense into a clean, checked container (§3.2).
   * @returns {Promise<void>}
   */
  async function toAdsense() {
    if (state.destroyed || state.adsenseDone) return;
    destroyGpt();
    // C14 / AS-8: one AdSense unit per slot per page view.
    state.adsenseDone = true;
    // 2.0.4 fail-safe: off unless enabled with a real publisher ID (ca-pub- + 16 digits) and a slot, so a
    // forgotten or placeholder config never shows an empty AdSense box.
    const adsense = config.fallbackAdsense;
    if (!adsense.enabled || !/^ca-pub-\d{16}$/.test(adsense.client) || !adsense.slot) return finalEmpty('adsense_disabled');
    state.tier = 'adsense';
    // Step 1 fresh node: replace the ad shell with a new empty div (slot keeps its height).
    const old = adShell();
    const fresh = doc.createElement('div');
    fresh.className = 'qsvp-ad-shell';
    if (old) old.replaceWith(fresh);
    else slot.querySelector('.qsvp-body').appendChild(fresh);
    deps.onState('fallback-adsense-pending', {});
    const consent = await deps.consentPromise; // PRV-7: AdSense only after the CMP is ready
    await deps.whenVisible(); // §3.2 step 2: >= 50% visible (assumption A19: wait, then check)
    if (state.destroyed) return;
    const failed = checkAdsenseContainer({
      container: fresh,
      slot,
      article: deps.article,
      win,
      overlapRects: deps.getOverlapRects(),
      visibleRatio: deps.getVisibleRatio(),
      adSize: adsenseSize(deps.getProfile()),
    });
    if (failed) {
      deps.onEvent('qsvp_adsense_blocked', { check: failed });
      deps.onState('all-empty', { reason: 'adsense_blocked' }); // neutral placeholder
      return;
    }
    // Step 3: fixed-size unit, standard code, loaded once from Google's URL.
    const [width, height] = adsenseSize(deps.getProfile());
    const ins = doc.createElement('ins');
    ins.className = 'adsbygoogle';
    ins.style.cssText = `display:inline-block;width:${width}px;height:${height}px`;
    ins.setAttribute('data-ad-client', adsense.client);
    ins.setAttribute('data-ad-slot', adsense.slot);
    fresh.appendChild(ins);
    deps.onState('fallback-adsense', {});
    const observer = new deps.MutationObserverCtor(() => {
      const status = ins.getAttribute('data-ad-status');
      if (!status) return;
      observer.disconnect();
      deps.onEvent('qsvp_fallback_adsense', { status });
      // Step 4: filled -> never touched again (C14). Unfilled -> collapse rule.
      if (status === 'unfilled') {
        ins.remove();
        finalEmpty('adsense_unfilled');
      }
    });
    observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
    try {
      const queue = (win.adsbygoogle = win.adsbygoogle || []);
      // §2.1: AdSense only after the CMP is ready (consent was resolved before the waterfall).
      if (consent && consent.npa) queue.requestNonPersonalizedAds = 1;
      if (deps.childDirected || (consent && consent.tfcd)) queue.tagForChildDirectedTreatment = 1;
      if (!doc.querySelector(`script[src^="${ADSENSE_URL}"]`)) {
        deps
          .loadScript(`${ADSENSE_URL}?client=${adsense.client}`, { crossorigin: 'anonymous' }) // client checked: ca-pub- + digits only
          .catch(() => {
            observer.disconnect();
            ins.remove();
            finalEmpty('adsense_blocked_script');
          });
      }
      queue.push({});
    } catch (error) {
      observer.disconnect();
      ins.remove();
      finalEmpty('adsense_error');
    }
  }

  /**
   * Tier 2: GPT display slot in the same reserved space (§3, §7.2).
   * Requested only while the slot is >= 50% visible (WF-6).
   * @returns {Promise<void>}
   */
  async function toDisplay() {
    if (state.destroyed || state.tier) return;
    state.tier = 'gpt';
    if (!display.enabled) return toAdsense();
    deps.onState('fallback-pending', {});
    await deps.whenVisible();
    if (state.destroyed) return;
    let googletag = win.googletag;
    if (!googletag || !googletag.apiReady) {
      try {
        win.googletag = win.googletag || { cmd: [] };
        if (!doc.querySelector(`script[src^="${GPT_URL}"]`)) await deps.loadScript(GPT_URL, {});
      } catch (error) {
        // ERR-6: GPT blocked -> AdSense if it can load, else placeholder/collapse.
        deps.onEvent('qsvp_player_error', { error_code: 'gpt_load', stage: 'display' });
        return toAdsense();
      }
      googletag = win.googletag;
    }
    const divId = `qsvp-gpt-${playerId}`;
    state.divId = divId;
    const shell = adShell();
    const div = doc.createElement('div');
    div.id = divId;
    shell.appendChild(div);
    deps.onState('fallback-display', {});
    // ERR-6 / §3: no render within 5 s counts as empty; destroy the slot first.
    state.renderTimer = deps.setTimeoutFn(() => {
      deps.onEvent('qsvp_fallback_display', { filled: false, size: 'timeout' });
      toAdsense();
    }, display.gptTimeoutMs);
    googletag.cmd = googletag.cmd || [];
    googletag.cmd.push(() => {
      try {
        if (state.destroyed || state.tier !== 'gpt') return;
        const pubads = googletag.pubads();
        const mapping = googletag.sizeMapping();
        for (const [viewport, sizes] of fallbackSizeMapping(config.sizeProfiles)) mapping.addSize(viewport, sizes);
        const slotObj = googletag.defineSlot(display.adUnit, display.sizes, divId);
        if (!slotObj) throw new Error('define_slot_failed');
        slotObj.defineSizeMapping(mapping.build());
        slotObj.setCollapseEmptyDiv(false); // §3: only the script collapses
        slotObj.setForceSafeFrame(true); // §7.2: no expanding creatives
        slotObj.setSafeFrameConfig({ allowOverlayExpansion: false, allowPushExpansion: false });
        const targeting = Object.assign({}, deps.targeting, { refresh: '0' });
        Object.keys(targeting).forEach((key) => targeting[key] && slotObj.setTargeting(key, String(targeting[key])));
        if (deps.childDirected) pubads.setPrivacySettings({ childDirectedTreatment: true }); // PRV-6
        slotObj.addService(pubads);
        state.gptSlot = slotObj;
        state.refresh = createRefreshTimer({
          refreshSeconds: display.refreshSeconds,
          maxRefreshes: display.maxRefreshes,
          now: deps.now,
          setTimeoutFn: deps.setTimeoutFn,
          clearTimeoutFn: deps.clearTimeoutFn,
          onRefresh: (count) => {
            slotObj.setTargeting('refresh', String(count)); // §5: refresh=1..5 key-value
            deps.onEvent('qsvp_display_refresh_request', { refresh_n: count });
            pubads.refresh([slotObj]);
          },
        });
        pubads.addEventListener('slotRenderEnded', (event) => onRenderEnded(event));
        pubads.addEventListener('slotVisibilityChanged', (event) => {
          if (event.slot === state.gptSlot && state.refresh) state.refresh.onVisibility(event.inViewPercentage);
        });
        googletag.enableServices();
        googletag.display(divId);
        if (typeof pubads.isInitialLoadDisabled === 'function' && pubads.isInitialLoadDisabled()) pubads.refresh([slotObj]);
      } catch (error) {
        deps.onEvent('qsvp_player_error', { error_code: 'gpt_define', stage: 'display' });
        toAdsense();
      }
    });
  }

  /**
   * GPT slotRenderEnded for our slot.
   * @param {object} event
   */
  function onRenderEnded(event) {
    if (event.slot !== state.gptSlot || state.tier !== 'gpt') return;
    deps.clearTimeoutFn(state.renderTimer);
    const refreshCount = state.refresh ? state.refresh.state.count : 0;
    if (refreshCount > 0) {
      deps.onEvent('qsvp_display_refresh', { refresh_n: refreshCount, filled: !event.isEmpty });
      if (event.isEmpty) {
        // REF-3: a refresh came back empty -> neutral placeholder, stop refreshing, no AdSense mid-view.
        state.refresh.onRender(false);
        deps.onState('all-empty', { reason: 'refresh_empty' });
        return;
      }
      state.refresh.onRender(true);
      return;
    }
    const size = Array.isArray(event.size) ? event.size.join('x') : '';
    deps.onEvent('qsvp_fallback_display', { filled: !event.isEmpty, size });
    if (event.isEmpty) return toAdsense(); // WF-7: display empty -> tier 3
    // §7.2: a creative taller than the reserved frame is logged (source gets blocked in GAM).
    if (Array.isArray(event.size) && event.size[1] > 280) {
      deps.onEvent('qsvp_player_error', { error_code: 'creative_oversize', stage: 'display' });
    }
    state.refresh.onRender(true);
    state.refresh.onTabVisibility(doc.visibilityState === 'visible');
  }

  return {
    state,
    toDisplay,
    toAdsense,
    /** @param {boolean} visible */
    onTabVisibility(visible) {
      if (state.refresh) state.refresh.onTabVisibility(visible);
    },
    destroy() {
      state.destroyed = true;
      destroyGpt();
    },
  };
}
